/**
 * useMLInsights Hook
 *
 * Custom hooks para gestionar ML Insights usando React Query
 * Soporta insights de Communications y Pipelines
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/shared/contexts/AuthContext';
import communicationsApi, { type ConversationMLInsight } from '@/services/api/communicationsApi';
import pipelinesApi, { type PipelineMLInsight, type PipelineMLInsightListParams } from '@/services/api/pipelinesApi';

// ============================================================================
// COMMUNICATIONS ML INSIGHTS
// ============================================================================

/**
 * Hook para obtener insights ML de conversaciones
 */
export function useConversationMLInsights(params?: {
  insight_type?: string;
  severity?: string;
  status?: string;
  conversation?: string;
  lead?: string;
  limit?: number;
  offset?: number;
}) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['ml-insights', 'conversations', params],
    queryFn: async () => {
      try {
        return await communicationsApi.mlInsights.list(params);
      } catch (error) {
        // Return empty structure on error instead of throwing
        return { count: 0, results: [] };
      }
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes — sidebar badges don't need real-time freshness
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook para obtener un insight específico de conversación
 */
export function useConversationMLInsight(id: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['ml-insights', 'conversations', id],
    queryFn: async () => {
      try {
        return await communicationsApi.mlInsights.get(id!);
      } catch (error) {
        // Return null on error to indicate insight not found
        return null;
      }
    },
    enabled: isAuthenticated && !!id,
    staleTime: 30 * 1000, // 30 segundos
  });
}

/**
 * Hook para marcar insight de conversación como revisado
 */
export function useMarkConversationInsightAsReviewed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => communicationsApi.mlInsights.markAsReviewed(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ml-insights', 'conversations'] });
    },
  });
}

/**
 * Hook para marcar insight de conversación como aplicado
 */
export function useMarkConversationInsightAsActedUpon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => communicationsApi.mlInsights.markAsActedUpon(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ml-insights', 'conversations'] });
    },
  });
}

/**
 * Hook para descartar insight de conversación
 */
export function useDismissConversationInsight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => communicationsApi.mlInsights.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ml-insights', 'conversations'] });
    },
  });
}

// ============================================================================
// PIPELINE ML INSIGHTS
// ============================================================================

/**
 * Hook para obtener insights ML de pipelines
 */
export function usePipelineMLInsights(params?: PipelineMLInsightListParams) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['ml-insights', 'pipelines', params],
    queryFn: async () => {
      try {
        return await pipelinesApi.mlInsights.list(params);
      } catch (error) {
        // Return empty structure on error instead of throwing
        return { count: 0, results: [] };
      }
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes — sidebar badges don't need real-time freshness
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook para obtener un insight específico de pipeline
 */
export function usePipelineMLInsight(id: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['ml-insights', 'pipelines', id],
    queryFn: async () => {
      try {
        return await pipelinesApi.mlInsights.get(id!);
      } catch (error) {
        // Return null on error to indicate insight not found
        return null;
      }
    },
    enabled: isAuthenticated && !!id,
    staleTime: 1 * 60 * 1000,
  });
}

/**
 * Hook para marcar insight de pipeline como revisado
 */
export function useReviewPipelineInsight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => pipelinesApi.mlInsights.review(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ml-insights', 'pipelines'] });
    },
  });
}

/**
 * Hook para implementar recomendación de pipeline insight
 */
export function useImplementPipelineInsight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      pipelinesApi.mlInsights.implement(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ml-insights', 'pipelines'] });
    },
  });
}

/**
 * Hook para descartar insight de pipeline
 */
export function useDismissPipelineInsight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      pipelinesApi.mlInsights.dismiss(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ml-insights', 'pipelines'] });
    },
  });
}

/**
 * Hook para generar insights manualmente
 */
export function useGeneratePipelineInsights() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pipelineId: string) => pipelinesApi.mlInsights.generate(pipelineId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ml-insights', 'pipelines'] });
    },
  });
}

/**
 * Hook para calcular probabilidades de stages
 */
export function useCalculateStageProbabilities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pipelineId: string) => pipelinesApi.mlInsights.calculateProbabilities(pipelineId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
      queryClient.invalidateQueries({ queryKey: ['ml-insights', 'pipelines'] });
    },
  });
}

// ============================================================================
// UNIFIED HOOKS (Ambos tipos de insights)
// ============================================================================

/**
 * Hook unificado para obtener todos los insights ML (conversaciones + pipelines)
 * Útil para dashboards que muestran insights de ambos tipos
 */
export function useAllMLInsights(params?: {
  severity?: string;
  status?: string;
  limit?: number;
}) {
  const conversationInsights = useConversationMLInsights({
    severity: params?.severity,
    status: params?.status,
    limit: params?.limit,
  });

  const pipelineInsights = usePipelineMLInsights({
    severity: params?.severity as any,
    status: params?.status as any,
    limit: params?.limit,
  });

  return {
    conversationInsights,
    pipelineInsights,
    isLoading: conversationInsights.isLoading || pipelineInsights.isLoading,
    isError: conversationInsights.isError || pipelineInsights.isError,
    allInsights: [
      ...(conversationInsights.data?.results || []),
      ...(pipelineInsights.data?.results || []),
    ],
    totalCount:
      (conversationInsights.data?.count || 0) + (pipelineInsights.data?.count || 0),
  };
}

/**
 * Hook para obtener top insights urgentes (high + critical)
 * Ideal para widgets de dashboard
 */
export function useUrgentInsights(limit: number = 5) {
  return useAllMLInsights({
    severity: 'high',
    status: 'new',
    limit,
  });
}
